import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { SegmentedControl, SegmentedControlItem, SkeletonText, Stack, StackItem, Typography } from '@optimus-web/core';
import { CardContainer } from '@mews/b2b-ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { AgeingBalance, AgeingDistribution } from '@mews-commander/data-access';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { AgeingItem } from './AgeingItem';
import { determineItemFitStyle, NORMAL } from './determineItemFitStyle';

interface AgeingCardProps {
    data?: AgeingDistribution | null;
    loading?: boolean;
    error?:
        | null
        | boolean
        | {
              message: React.ReactNode;
          };
    currencySymbol: string;
    date?: string;
}

enum ViewMode {
    Percentage = 'percentage',
    Currency = 'currency',
}

const labelMapping: Record<keyof AgeingBalance, string> = {
    lessThan30: '0-30',
    upTo60: '31-60',
    upTo90: '61-90',
    plus90: '90+',
};

const PERCENTAGE_LABEL = UnicodeEntity.Percent;

export const AgeingCard = ({ data, loading, error, currencySymbol, date }: AgeingCardProps) => {
    const [view, setView] = useState<ViewMode>(ViewMode.Percentage);
    const [isCompactMode, setIsCompactMode] = useState(NORMAL);

    const cardRef = useRef<HTMLDivElement>(null);
    const childWrapperRef = useRef<HTMLDivElement>(null);

    const checkFit = useCallback(() => {
        if (!cardRef?.current || !childWrapperRef?.current) {
            return;
        }

        const parentWidth = cardRef.current.clientWidth || 0;
        const totalChildWidth = childWrapperRef.current.clientWidth || 0;

        const fitStyle = determineItemFitStyle(parentWidth, totalChildWidth);
        if (fitStyle && JSON.stringify(fitStyle) !== JSON.stringify(isCompactMode)) {
            setIsCompactMode(fitStyle);
        }
    }, [isCompactMode]);

    // Use `useLayoutEffect` to measure and adjust before painting to the screen
    useLayoutEffect(() => {
        checkFit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    return (
        <CardContainer data-test-id="aging-card">
            <Stack vertical spacing="100">
                <Stack distribution="space-between" itemAlignment="center">
                    <StackItem>
                        <Typography data-test-id="title" textStyle="bodyLargeStrong" as="div">
                            <LocalizedText textKey={TextKey.Aging} />
                        </Typography>
                        {date && (
                            <Typography data-test-id="date" textStyle="bodySmall" variant="tertiary" as="p">
                                <LocalizedDateFromUtc utcString={date} />
                            </Typography>
                        )}
                    </StackItem>
                    <SegmentedControl
                        value={view}
                        onChange={(_e, value) => setView(value as ViewMode)}
                        size="small"
                        name="aging-toggle"
                        disabled={loading || !!error}
                        itemWidth="content"
                    >
                        <SegmentedControlItem
                            data-test-id="percentage-control-item"
                            label={PERCENTAGE_LABEL}
                            value={ViewMode.Percentage}
                        />
                        <SegmentedControlItem
                            data-test-id="currency-control-item"
                            label={currencySymbol}
                            value={ViewMode.Currency}
                        />
                    </SegmentedControl>
                </Stack>
                <Stack ref={cardRef}>
                    {loading ? <SkeletonText data-test-id="aging-skeleton-text" width="66%" /> : null}
                    {!loading && !error && (
                        <Stack spacing="200" ref={childWrapperRef}>
                            {(Object.keys(labelMapping) as Array<keyof typeof labelMapping>).map((key) => (
                                <AgeingItem
                                    key={key}
                                    daysAging={labelMapping[key]}
                                    value={{
                                        percentage: data?.ageingPercentage?.[key],
                                        currency: data?.ageingBalance?.[key],
                                    }}
                                    view={view}
                                    isCompactMode={isCompactMode}
                                />
                            ))}
                        </Stack>
                    )}
                    {!loading && error && (
                        <Stack itemAlignment="center" alignment="center" distribution="stretch" fill>
                            <Typography textStyle="bodySmall" align="center" as="div">
                                {typeof error === 'object' && error.message}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </CardContainer>
    );
};
